import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    GoogleMap,
    DirectionsRenderer,
    useJsApiLoader,
    Marker,
    InfoWindow, DirectionsService
} from "@react-google-maps/api";
import _ from 'lodash';
import moment from "moment";
import {Loader} from "components";
import {EscortAppPatientEnum} from "../common";
import {Notify} from "utils";

const containerStyle = {
    width: "100%",
    height: "100%",
    borderRadius: "8px 8px 0 0",
};

const center = {lat: 34.0522, lng: -118.2437};
const libraries = ['places', 'geometry'];

export const GoogleMapComponent = memo(({vehiclesLocations = [], legLocation, mapView}) => {
    const {isLoaded} = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        libraries,
    });
    const [map, setMap] = useState(null);
    const [currentVehicleLocation, setCurrentVehicleLocation] = useState(null);
    const [origin, setOrigin] = useState(null);
    const [destination, setDestination] = useState(null);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [directions, setDirections] = useState(null);
    const [routeInfo, setRouteInfo] = useState(null);
    const [midpoint, setMidpoint] = useState(null);
    const [showInfo, setShowInfo] = useState({
        origin: false,
        destination: false
    });
    const originMarkerRef = useRef(null);
    const destinationMarkerRef = useRef(null);
    const vehiclesMarkerRef = useRef({});


    useEffect(() => {
        if (directions && map) {
            const bounds = new window.google.maps.LatLngBounds();
            directions.routes[0].overview_path.forEach(p => bounds.extend(p));
            map.fitBounds(bounds);
        }
    }, [directions, map]);

    useEffect(() => {
        setDirections(null);
        setRouteInfo(null);
        setMidpoint(null);
    }, [legLocation, origin, destination]);

    useEffect(() => {
        const vehicle = vehiclesLocations?.find(v => v.vehicleId === legLocation?.vehicleId) || null;
        setCurrentVehicleLocation(vehicle);
    }, [vehiclesLocations, legLocation]);

    useEffect(() => {
        if (EscortAppPatientEnum.OPEN !== legLocation?.legStatus) {
            if (currentVehicleLocation?.vehicleLat && currentVehicleLocation?.vehicleLng) {
                setOrigin({lat: currentVehicleLocation.vehicleLat, lng: currentVehicleLocation.vehicleLng});
            } else {
                setOrigin(legLocation?.pickUp || null);
            }
        } else {
            setOrigin(legLocation?.pickUp || null);
        }
    }, [legLocation, currentVehicleLocation]);

    useEffect(() => {
        setDestination(legLocation?.legStatus === EscortAppPatientEnum.ASSIGNED ? legLocation?.pickUp : legLocation?.dropOff);
    }, [legLocation]);


    const vehicleMatch = useMemo(() => {
        if (!origin) return null;
        return vehiclesLocations?.find(v => v.vehicleLat === origin.lat && v.vehicleLng === origin.lng);
    }, [vehiclesLocations, origin]);

    const getDepartureTime = () => {
        const now = moment.utc();
        let departure = null;

        if (legLocation?.legStatus === EscortAppPatientEnum.OPEN && legLocation?.pickUpDate) {
            departure = moment.utc(legLocation.pickUpDate);
        }

        if (!departure || departure.isSameOrBefore(now)) {
            departure = now.add(2, 'minutes');
        }

        return departure.toDate();
    };

    const toggleShowInfo = (key) => {
        setShowInfo(prev => ({...prev, [key]: !prev[key]}));
    };

    const onMarkerClick = (vehicle) => {
        setSelectedVehicle(vehicle);
    };

    const onLoad = useCallback((map) => {
        setMap(map);
    }, []);

    const onUnmount = useCallback(() => {
        setMap(null);
    }, []);

    if (!isLoaded) {
        return (
            <div className="flex-all-center" style={{width: '100%', height: '100%'}}>
                <Loader styles={{width: 80, height: 80}}/>
            </div>
        );
    }

    return (
        <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={12}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={{
                streetViewControl: false,
                fullscreenControl: false,
                mapTypeControl: false,
            }}
        >
            {vehiclesLocations
                ?.filter((v) => {
                    const latLngEqual = (a, b) => a?.lat === b?.lat && a?.lng === b?.lng;
                    const loc = {lat: v.vehicleLat, lng: v.vehicleLng};
                    return !latLngEqual(loc, origin) && !latLngEqual(loc, destination);
                })
                .map((vehicle) => (
                    <Marker
                        key={vehicle.vehicleId}
                        position={{lat: vehicle.vehicleLat, lng: vehicle.vehicleLng}}
                        onClick={(e) => {
                            e.stop();
                            if (e?.domEvent?.stopPropagation) {
                                e.domEvent.stopPropagation();
                            }
                            onMarkerClick(vehicle);
                            setShowInfo({origin: false, destination: false});
                        }}
                        onLoad={(marker) => {
                            if (vehicle.vehicleId) {
                                vehiclesMarkerRef.current[vehicle.vehicleId] = marker;
                            }
                        }}
                        label={{
                            text: vehicle.unitNumber.toString(),
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 'bold',
                        }}
                        options={{
                            optimized: true,
                        }}
                        zIndex={999}
                    />
                ))}

            {origin?.lat && origin?.lng && (
                <Marker
                    position={origin}
                    onLoad={(marker) => (originMarkerRef.current = marker)}
                    onClick={(e) => {
                        e.stop();
                        if (vehicleMatch) {
                            onMarkerClick(vehicleMatch);
                        } else {
                            toggleShowInfo('origin');
                        }
                    }}
                    label={{
                        text: vehicleMatch?.unitNumber?.toString() || 'A',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 'bold',
                    }}
                    zIndex={999}
                />
            )}

            {destination?.lat && destination?.lng && (
                <Marker
                    position={destination}
                    onLoad={(marker) => (destinationMarkerRef.current = marker)}
                    onClick={(e) => {
                        e.stop();
                        toggleShowInfo('destination')
                    }}
                    label={{
                        text: 'B',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 'bold',
                    }}
                    zIndex={999}

                />
            )}

            {origin && showInfo.origin && (
                <InfoWindow
                    position={origin}
                    anchor={originMarkerRef.current}
                    onCloseClick={() => toggleShowInfo('origin')}
                    options={{
                        disableAutoPan: true,
                        pixelOffset: new window.google.maps.Size(0, -10)
                    }}
                >
                    <div style={{maxWidth: '200px'}}>
                        <h4>{origin?.formattedAddress}</h4>
                    </div>
                </InfoWindow>
            )}

            {destination && showInfo.destination && (
                <InfoWindow
                    position={destination}
                    anchor={destinationMarkerRef.current}
                    onCloseClick={() => toggleShowInfo('destination')}
                    options={{
                        disableAutoPan: true,
                        pixelOffset: new window.google.maps.Size(0, -10)
                    }}
                >
                    <div style={{maxWidth: '200px'}}>
                        <h4>{destination?.formattedAddress}</h4>
                    </div>
                </InfoWindow>
            )}

            {selectedVehicle?.vehicleId && vehiclesMarkerRef.current?.[selectedVehicle.vehicleId] && (
                <InfoWindow
                    key={selectedVehicle.vehicleId}
                    position={{
                        lat: selectedVehicle.vehicleLat,
                        lng: selectedVehicle.vehicleLng,
                    }}
                    anchor={vehiclesMarkerRef.current[selectedVehicle.vehicleId]}
                    onCloseClick={() => setSelectedVehicle(null)}
                    options={{
                        disableAutoPan: true,
                        pixelOffset: new window.google.maps.Size(0, -10)
                    }}
                >
                    <div style={{maxWidth: '200px'}}>
                        <h4>Vehicle: {selectedVehicle.unitNumber}</h4>
                    </div>
                </InfoWindow>
            )}

            {(origin && destination && !directions) && (
                <DirectionsService
                    options={{
                        origin: origin,
                        destination: destination,
                        travelMode: window.google.maps.TravelMode.DRIVING,
                        drivingOptions: {
                            departureTime: getDepartureTime(),
                            trafficModel: 'bestguess',
                        },
                    }}
                    callback={(result, status) => {
                        if (status === window.google.maps.DirectionsStatus.OK) {
                            setDirections(result);

                            const leg = result.routes[0].legs[0];
                            setRouteInfo({
                                distance: leg.distance.text,
                                duration: leg.duration.text,
                            });

                            const steps = leg.steps;
                            const midStep = steps[Math.floor(steps.length / 2)];
                            const midLocation = midStep?.start_location;
                            if (midLocation) setMidpoint(midLocation);
                        } else {
                            const warnMsg = 'No directions found for the selected route. Please check the origin and destination.';
                            setOrigin(legLocation?.pickUp || null);

                            if (status === 'ZERO_RESULTS') {
                                if (legLocation?.legStatus !== EscortAppPatientEnum.OPEN) {
                                    const errorText = legLocation?.legStatus === EscortAppPatientEnum.ASSIGNED ? 'No directions found for the selected route. Please check the assigned vehicle location and leg origin.'
                                        : 'No directions found for the selected route. Please check the assigned vehicle location and leg destination.'
                                    Notify(currentVehicleLocation
                                        ? errorText
                                        : warnMsg, 'warning');
                                } else {
                                    Notify(warnMsg, 'warning');
                                }
                            } else {
                                Notify(warnMsg, 'warning');
                            }
                        }
                    }}
                />
            )}

            {origin && destination && directions && (
                <DirectionsRenderer
                    options={{
                        directions,
                        suppressMarkers: true,
                        preserveViewport: true,
                        polylineOptions: {
                            strokeColor: '#4285F4',
                            strokeOpacity: 1,
                            strokeWeight: 4,
                        },
                    }}
                />
            )}

            {midpoint && routeInfo && (
                <InfoWindow
                    position={midpoint}
                    options={{
                        pixelOffset: new window.google.maps.Size(0, -10),
                        disableAutoPan: true,
                    }}
                >
                    <div style={{fontSize: 14}}>
                        <div><strong>Distance:</strong> {routeInfo.distance}</div>
                        <div><strong>Duration:</strong> {routeInfo.duration}</div>
                    </div>
                </InfoWindow>
            )}
        </GoogleMap>
    );
}, areEqual);

function areEqual(prevProps, nextProps) {
    return (
        _.isEqual(prevProps.legLocation, nextProps.legLocation) &&
        _.isEqual(prevProps.vehiclesLocations, nextProps.vehiclesLocations)
    );
}

